import {
  useLessonPagesState,
  LessonPagesActions,
  useLessonPagesDispatch,
} from "../contexts/LessonPagesProvider/LessonPagesProvider";
import { ObjectActionsType, useObjectsDispatch, useObjectsState } from "../contexts/ObjectsProvider";
import { useTimelineDispatch } from "../contexts/TimelineProvider/TimelineProvider";
import { useMetaVariableStore } from "../lib/SmartObject/store";
import { useAudioManagerStore } from "../contexts/PageAudioManager";
import { useLessonStore } from "../store/lesson.store";
import { audioBlobToApi } from "../panels/TimelinePanel/helpers";
import { getSortedAudios } from "../contexts/PageAudioManager/helpers";
import { NarrationAudio, EffectAudio } from "../models/IInteractiveAudio";
import { isTimelineEnabled } from "../utils/Lesson";
import { getVariablesFromSmartObject } from "../lib/SmartObject";
import ILessonPageDto from "../models/ILessonPageDto";

export function useGoToPage(lessonPages: ILessonPageDto[]) {
  const lessonPagesState = useLessonPagesState();
  const lessonPagesDispatch = useLessonPagesDispatch();
  const objectsState = useObjectsState();
  const objectsDispatch = useObjectsDispatch();
  const timelineDispatch = useTimelineDispatch();

  /**
   * Meta Variables
   */

  const addNewMetaVariable = useMetaVariableStore((s) => s.addNewMetaVariable);
  const clearMetaVariables = useMetaVariableStore((s) => s.clearAllMetaVariables);
  const clearAnimatedMetaVariables = useMetaVariableStore((s) => s.clearAnimatedMetaVariables);
  const loadStore = useMetaVariableStore((s) => s.loadStore);

  return (pageIndexToDisplay: number, options?: Readonly<{ aggregationOrder?: number }>) => {
    // Get the current page to check if it supports advanced properties
    const currentPage =
      lessonPagesState.currentAggregationOrder !== null
        ? lessonPagesState.lessonPages[lessonPagesState.currentAggregationOrder]?.pageManifest?.Pages?.[
            lessonPagesState.currentLessonPageIndex
          ]
        : lessonPagesState.lessonPages[lessonPagesState.currentLessonPageIndex];
    const currentPageSupportsTimeline = isTimelineEnabled(currentPage);

    // we save all edits made to the objects on the page before switching pages
    const scorm = objectsState.objectList.find((object) => object.type === "SCORM");

    // Get current audio state before switching pages
    const { narrationAudios, audioEffects } = useAudioManagerStore.getState();

    lessonPagesDispatch({
      type: LessonPagesActions.SET_OBJECTS_ON_PAGE_MANIFEST,
      payload: {
        objects: objectsState.objectList,
        animatedObjects: objectsState.animatedObjects,
        images: objectsState.images,
        textBoxes: objectsState.textBoxes,
        tables: objectsState.tables,
        annotations: objectsState.annotations,
        videos: objectsState.videos,
        hotspots: objectsState.hotspots,
        scorm,
        smartObjects: objectsState?.smartObjects,
        panoramicList: objectsState.panoramicList,
        shapeList: objectsState.shapeList,
        narrationAudios,
        audioEffects,
      },
    });

    /**
     * We check for meta variables in current page and save them
     * to the current page manifest - only for freeForm pages that support smartObjects
     */
    const { metaVariables, metaVariablesData, animatedMetaVariables } = useMetaVariableStore.getState();

    if (metaVariables && currentPageSupportsTimeline) {
      lessonPagesDispatch({
        type: LessonPagesActions.UPDATE_CURRENT_PAGE_MANIFEST,
        payload: {
          metaVariables,
          metaVariablesData,
          animatedMetaVariables,
        },
      });
    }

    /**
     * We clear the meta variables before going into a new page
     * to make sure that the zustand store doesnt get too loaded
     * with all the meta variables from all the pages
     */
    clearMetaVariables(); // probably not needed ?
    clearAnimatedMetaVariables();

    // we switch the currently selected page from the lesson pages
    lessonPagesDispatch({
      type: LessonPagesActions.SET_CURRENT_PAGE_INDEX,
      payload: pageIndexToDisplay,
    });

    if (lessonPagesState.currentAggregationOrder !== options?.aggregationOrder) {
      lessonPagesDispatch({
        type: LessonPagesActions.SET_CURRENT_AGGREGATION_ORDER,
        payload: options?.aggregationOrder ?? null,
      });
    }

    /**
     * Get the page to display based on navigation context.
     * Note: aggregationOrder is guaranteed to equal array index due to referenceIdLogic in builder.tsx
     */
    let pageToDisplay;
    if (options?.aggregationOrder !== undefined && options?.aggregationOrder !== null) {
      // Navigating to a page inside an aggregation (microlesson)
      const aggregationPage = lessonPages[options.aggregationOrder];
      if (!aggregationPage?.pageManifest?.Pages?.[pageIndexToDisplay]) {
        console.warn(
          `[useGoToPage] Invalid aggregation navigation: aggregationOrder=${options.aggregationOrder}, pageIndex=${pageIndexToDisplay}`,
        );
        return;
      }
      pageToDisplay = aggregationPage.pageManifest.Pages[pageIndexToDisplay];
    } else {
      // Navigating to a regular page
      if (!lessonPages[pageIndexToDisplay]) {
        console.warn(`[useGoToPage] Invalid page navigation: pageIndex=${pageIndexToDisplay}`);
        return;
      }
      pageToDisplay = lessonPages[pageIndexToDisplay];
    }

    const newPageManifest = {
      ...pageToDisplay.pageManifest,
      // tables: OLD_STATE_pageManifest.tables,
    };

    /**
     * We check for meta variables in the page we're going to
     */
    let newPageMetaVariables = newPageManifest.metaVariables;
    let newPageMetaVariablesData = newPageManifest.metaVariablesData;
    const newPageAnimatedMetaVariables = newPageManifest.animatedMetaVariables;

    const newPageSmartObjects = newPageManifest.smartObjects ?? [];

    /**
     * If the new page has a smart object but no meta variables.
     * It probably means this lesson was imported with an external tool
     * that didn't properly loaded the meta variables into the page manifest.
     * so we're going to added them back from the smart objects.
     *
     * This process originally happens in the asset library when a SMO is added
     * into a page for the first time.
     */

    if (newPageSmartObjects.length > 0 && !newPageMetaVariables) {
      for (const smartObject of newPageSmartObjects) {
        const { variables, variablesData, fonts } = getVariablesFromSmartObject(
          smartObject.elementList[0],
          smartObject.objectId,
        );

        /**
         * Here "add" each variable just in case we need to merge with existing ones
         * in the future.
         */

        for (const key of Object.keys(variables)) {
          addNewMetaVariable({
            ...variablesData[key],
            key,
            value: variables[key],
          });
        }

        if (fonts) {
          for (const key of Object.keys(fonts)) {
            const fontVal = fonts[key];
            addNewMetaVariable({
              ...fontVal,
              key,
              value: fontVal,
            });
          }
        }
      }

      // now we re assign the new meta variables after recently adding them
      // to the zustand store. This will override any other existing ones, coming
      // from the page manifest.
      newPageMetaVariables = useMetaVariableStore.getState().metaVariables;
      newPageMetaVariablesData = useMetaVariableStore.getState().metaVariablesData;
    }

    /**
     * This replaces the whole store with the new meta variables and
     * meta variables data, this will delete the old meta variables
     * if there were any.
     */
    if (newPageMetaVariables) {
      loadStore(newPageMetaVariables, newPageMetaVariablesData, newPageAnimatedMetaVariables ?? []);
    } else {
      clearMetaVariables();
    }

    // we create a new object list from the new page manifest
    objectsDispatch({
      type: ObjectActionsType.SET_OBJECTS_FROM_PAGE_MANIFEST,
      payload: newPageManifest,
    });

    // Calculate sequenceLength: use manifest value but ensure it's large enough to contain all animated objects and meta variables
    const animatedObjects = newPageManifest?.timeline?.animatedObjects ?? [];
    const animatedMetaVars = newPageManifest?.animatedMetaVariables ?? [];

    // Find max extent from timeline entries (animated objects and meta variables)
    const getMaxExtent = (entries: Array<{ start?: number; end?: number | null }>): number => {
      let maxExtent = 0;
      for (const entry of entries) {
        const entryEnd = entry.end ?? entry.start ?? 0;
        const entryStart = entry.start ?? 0;
        maxExtent = Math.max(maxExtent, entryEnd, entryStart);
      }
      return maxExtent;
    };

    const maxObjectExtent = getMaxExtent(animatedObjects);
    const maxMetaVarExtent = getMaxExtent(animatedMetaVars);

    const manifestSequenceLength =
      newPageManifest?.timeline?.sequenceLength ?? newPageManifest?.timeline?.timelineLength ?? 30;
    // Use the larger of: manifest sequenceLength or the max extent from objects or meta variables (with a small buffer)
    const sequenceLength = Math.max(
      manifestSequenceLength,
      Math.ceil(maxObjectExtent) + 1,
      Math.ceil(maxMetaVarExtent) + 1,
    );

    timelineDispatch({
      type: "SET_SEQUENCE_LENGTH",
      payload: sequenceLength,
    });
    timelineDispatch({
      type: "SET_RANGE",
      payload: {
        start: 0,
        end: 25,
      },
    });
    // Reset scrub
    timelineDispatch({
      type: "SET_SECONDS",
      payload: 0,
    });
    // Reset audio stores and load audio from the new page manifest
    // Use getState() to avoid stale closure issues and potential undefined state during re-renders
    const { resetNarration, resetEffects, loadNarrationAudio, loadEffectAudio } = useAudioManagerStore.getState();

    resetNarration();
    resetEffects();

    // Set the current page version ID in LessonStore for tracking deleted audio assets
    useLessonStore.getState().setCurrentPageVersionId(pageToDisplay.pageVersionId ?? null);

    // Load narration audio from the new page manifest
    const audiosNarrationToLoad = getSortedAudios([...(newPageManifest?.narrationAudios ?? [])]) as NarrationAudio[];
    for (const audio of audiosNarrationToLoad) {
      loadNarrationAudio(audio.objectId, audioBlobToApi(audio.input as string), {
        ...audio,
      });
    }

    // Load effect audio from the new page manifest
    const audiosEffectsToLoad = getSortedAudios([...(newPageManifest?.audioEffects ?? [])]) as EffectAudio[];
    for (const audio of audiosEffectsToLoad) {
      loadEffectAudio(audio.objectId, audioBlobToApi(audio.input as string), {
        ...audio,
      });
    }
  };
}
